/**
 * 허브 출력 보기(SR-RUN-03 '허브 빛·소리는 셸 이벤트로 표시' · FS-03 · FS-04 로컬) — 커널 hub 이벤트(owner "me") → 작은 HUD 상태.
 * 순수 함수 · 시험 대상. 칠판(FS-12)은 룸 이벤트로 따로 모은다(features/board) — 색 표는 여기 하나를 같이 쓴다.
 *
 *  - light: 가운데 불빛(block-runtime 1.1.0 centerLight — off · red · orange · yellow · green · blue · violet · white). "off" 는 지운다
 *  - display: 5×5 화면(25자 밝기 0~9) · "text:<글자>"(흘러가는 글자). "say:<줄>"(print)은 콘솔 줄이라 HUD 에 두지 않는다
 *  - beep: 소리 — 마지막으로 난 시각(보이는 시간은 부르는 쪽이 정한다)
 */
import type { KernelEvent } from "@picnic-field/local-host/protocol";

export interface HubView {
  readonly light: string | null;
  readonly display: string | null;
  readonly beepAt: number | null;
}

export const HUB_IDLE: HubView = { light: null, display: null, beepAt: null };
/** 소리 표시가 남는 시간(ms) */
export const HUB_BEEP_SHOW_MS = 800;

/** 허브 빛 이름 → 색(SPIKE 색 이름 · 없으면 CSS 색 그대로 · 모르면 노랑) */
export const HUB_COLORS: Readonly<Record<string, string>> = {
  red: "#D64545",
  green: "#2E9E63",
  blue: "#2F5D8C",
  azure: "#4AA3DF",
  yellow: "#FFC845",
  orange: "#F08A24",
  white: "#FFFFFF",
  violet: "#8A5CC7",
  magenta: "#C74A9C",
};
export const hubColor = (light: string) =>
  HUB_COLORS[light.toLowerCase()] ?? (/^#[0-9a-f]{3,8}$/i.test(light) ? light : "#FFC845");

/** 커널 이벤트 하나 → 새 보기(내 기계 hub 이벤트만 — 나머지는 그대로 돌려준다) */
export function onHubEvent(v: HubView, e: KernelEvent, now: number): HubView {
  if (e.type !== "hub" || e.owner !== "me") return v;
  let next = v;
  if (typeof e.light === "string")
    next = { ...next, light: e.light && e.light !== "off" ? e.light : null };
  if (typeof e.display === "string" && !e.display.startsWith("say:"))
    next = { ...next, display: e.display };
  if (e.beep === true) next = { ...next, beepAt: now };
  return next;
}

/** 5×5 화면 25자 → 칸 밝기(0~9) · 화면 모양이 아니면 null */
export function hubPixels(display: string | null): number[] | null {
  return display && /^[0-9]{25}$/.test(display) ? [...display].map(Number) : null;
}

/** HUD 에 보일 것이 있나(불빛 · 켜진 칸 · 글자 · 방금 난 소리) */
export function hubVisible(v: HubView, now: number): boolean {
  const px = hubPixels(v.display);
  return (
    v.light !== null ||
    (px !== null && px.some((b) => b > 0)) ||
    (v.display?.startsWith("text:") ?? false) ||
    (v.beepAt !== null && now - v.beepAt < HUB_BEEP_SHOW_MS)
  );
}
